<template>
  <client-only>
    <div v-show="showPromotion">
      <section
        id="campaign"
        class="bg-cover"
        :style="{ backgroundImage: `url(${$assetsUrl(backgroundImageUrl)})`, backgroundPosition: backgroundPosition }"
      >
        <div class="co-container-lg flex flex-col items-center px-4 py-20 text-center">
          <div class="co-display-10 m-auto block w-3/4 md:w-3/4">
            <img
              class="te-img-campaign-title mx-auto mb-7 sm:px-3 md:px-0"
              :src="titleImageUrl"
              :alt="titleImageAlt"
              loading="lazy"
            >
            <TeCommonBalloon
              v-if="alertLimited"
              class="mb-5"
              :style="{ borderColor: textBgColor, backgroundColor: textBgColor, width: '16rem' }"
            >
              <span class="text-white">残りわずか！お早めに</span>
            </TeCommonBalloon>
            <div
              class="co-ribbon-up mb-5 block text-co-gray-50"
              :style="{ borderColor: textBgColor }"
            >
              <div
                class="co-ribbon-up-inner"
                :style="{ backgroundColor: textBgColor }"
              >
                <div
                  class="co-ribbon-up-content"
                  :style="{ borderColor: `transparent transparent ${subTextBgColor} transparent}` }"
                >
                  <span>
                    <FontAwesomeIcon
                      :icon="faClockNine"
                      class="fa-fw hidden pr-3 sm:inline"
                    />
                    <span class="hidden sm:inline">キャンペーン</span>
                    締切
                    <FontAwesomeIcon
                      :icon="faCaretRight"
                      class="fa-fw pl-2 md:pl-3"
                    />
                  </span>
                  <span>
                    <b class="px-1 md:px-2">{{ endsMonth }}</b>
                    月
                    <b class="px-1 md:px-2">{{ endsDays }}</b>
                    日（{{ endsDayOfWeek }}）26時
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div>
            <p
              class="mb-4"
              :style="{ textShadow: textShadow }"
            >
              <span
                v-for="desc in descriptions"
                class="inline sm:block"
                :style="{ color: textColor }"
              >{{ desc }}</span>
            </p>
            <a
              class="co-btn-ghost-white mb-4 rounded-full border-2 border-co-gray-700"
              :style="{ borderColor: textColor, color: textColor, textShadow: textShadow, boxShadow: boxShadow }"
              href="#apply"
              @click.prevent="scrollTo('apply')"
            >お申し込みフォームへ</a>
            <ul
              class="co-display-13 block"
              :style="{ textShadow: textShadow }"
            >
              <li :style="{ color: textColor }">
                ※キャンペーン期間内に {{ weeksText }} のいずれかのプランにお申し込みの方が対象になります。
              </li>
              <li
                v-if="firstSidejobLitePlan"
                :style="{ color: textColor }"
              >
                ※はじめての副業コース12週間Liteプランは キャンペーンの対象外です。
              </li>
              <li
                v-for="caus in cautions"
                :style="{ color: textColor }"
              >
                {{ caus }}
              </li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  </client-only>
</template>

<script lang="ts">
import { formatDayOfWeek } from '~/utility/utility'
import TeCommonBalloon from '~/components/Te/TeCommon/Balloon/TeCommonBalloon/TeCommonBalloon.vue'

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faCaretRight } from '@awesome.me/kit-65d95819ce/icons/classic/solid'
import { faClockNine } from '@awesome.me/kit-65d95819ce/icons/classic/regular'

const options = {
  async setup() {
    return {
      faClockNine,
      faCaretRight,
    }
  },
  props: {
    currentPromotion: {
      type: Object,
    },
    firstSidejobLitePlan: {
      type: Boolean,
      default: false,
    },
    courseNamePath: {
      type: String,
      default: '',
    },
  },
  computed: {
    endsAt(): string {
      return this.currentPromotion?.attributes?.ends_at || '2050/01/01 02:00:00'
    },
    backgroundImageUrl(): string {
      return this.currentPromotion?.attributes?.background_image_url || ''
    },
    backgroundPosition(): string {
      return this.currentPromotion?.attributes?.background_position || 'center'
    },
    textColor(): string {
      return this.currentPromotion?.attributes?.text_color
        ? `#${this.currentPromotion?.attributes?.text_color}`
        : '#ffffff'
    },
    textBgColor(): string {
      return this.currentPromotion?.attributes?.text_bg_color
        ? `#${this.currentPromotion?.attributes?.text_bg_color}`
        : '#000000'
    },
    dateEndsAt(): Date {
      return new Date(this.endsAt)
    },
    alertLimited(): null | false {
      return this.currentPromotion?.attributes?.alert_limited || false
    },
    titleImageUrl(): string {
      const { $assetsUrl } = useNuxtApp()
      return $assetsUrl(this.currentPromotion?.attributes?.title_image_url) || ''
    },
    titleImageAlt(): string {
      return this.currentPromotion?.attributes?.title_image_alt || ''
    },

    subTextBgColor(): string {
      return this.currentPromotion?.attributes?.sub_text_bg_color
        ? `#${this.currentPromotion?.attributes?.sub_text_bg_color}`
        : '#000000'
    },
    textShadow(): string {
      return this.currentPromotion?.attributes?.text_shadow || 'none'
    },
    boxShadow(): string {
      return this.currentPromotion?.attributes?.box_shadow || 'none'
    },
    ribbonColor(): string {
      return this.currentPromotion?.attributes?.text_bg_color
        ? `#${this.currentPromotion?.attributes?.text_bg_color}`
        : '#000000'
    },
    ribbonSubColor(): string {
      return this.currentPromotion?.attributes?.sub_text_bg_color
        ? `#${this.currentPromotion?.attributes?.sub_text_bg_color}`
        : '#000000'
    },
    descriptions(): string[] {
      return (
        this.currentPromotion?.attributes?.description?.split('\n') || [
          'キャンペーンの説明',
        ]
      )
    },
    cautions(): string[] {
      return (
        this.currentPromotion?.attributes?.caution?.split('\n') || [
          'キャンペーンの注意点',
        ]
      )
    },
    weeks(): string[] {
      return this.currentPromotion?.attributes?.weeks || ['-']
    },
    endsDays(): number {
      if (this.dateEndsAt.getDate() === 1) {
        const yesterday = new Date(this.dateEndsAt)
        yesterday.setDate(yesterday.getDate() - 1)
        return yesterday.getDate()
      }
      return this.dateEndsAt.getDate() - 1
    },
    endsMonth(): number {
      if (this.dateEndsAt.getDate() === 1) {
        const yesterday = new Date(this.dateEndsAt)
        yesterday.setDate(yesterday.getDate() - 1)
        return yesterday.getMonth() + 1
      }
      return this.dateEndsAt.getMonth() + 1
    },
    endsDayOfWeek(): String {
      const yesterday = this.dateEndsAt
      yesterday.setDate(yesterday.getDate() - 1)
      return formatDayOfWeek(yesterday)
    },
    weeksText(): string {
      return this.weeks
        .map((week) => {
          return `${week}週間`
        })
        .join(', ')
    },
    showPromotion(): boolean {
      return this.currentPromotion
        ? this.currentPromotion.attributes.screen_names.includes(
            this.courseNamePath
          )
        : false
    },
  },
  components: {
    TeCommonBalloon,
    FontAwesomeIcon,
  },
}

export default defineNuxtComponent(options)
</script>

<style scoped>
.te-img-campaign-title {
  @media (min-width: 992px) {
    max-width: 700px;
  }
}

/* // リボン上帯 */
.co-ribbon-up {
  position: relative;
  max-width: 280px;
  height: 2.75rem;
  margin-right: auto;
  margin-left: auto;
  font-size: 0.75rem;
  text-align: center;
  @media (min-width: 576px) {
    max-width: 400px;
  }
  @media (min-width: 768px) {
    max-width: 700px;
  }
  @media (min-width: 768px) {
    font-size: 1rem;
  }
  &::before,
  &::after {
    position: absolute;
    top: -0.5rem;
    display: block;
    content: '';
    border: 1rem solid;
    border-color: inherit;
  }
  &::before {
    left: -1.25rem;
    border-right-width: 1rem;
    border-left-color: transparent;
  }
  &::after {
    right: -1.25rem;
    border-right-color: transparent;
    border-left-width: 1rem;
  }
}

.co-ribbon-up-inner {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  z-index: 2;
  padding: 0.25rem 0;
  background-color: #ee2576;
}

.co-ribbon-up-content {
  &::before,
  &::after {
    position: absolute;
    top: -0.5rem;
    z-index: 1;
    display: block;
    content: '';
    border-color: inherit;
    border-style: solid;
  }
  &::before {
    left: 0;
    border-width: 0 0 0.5rem 0.75rem;
  }
  &::after {
    right: 0;
    border-width: 0 0.75rem 0.5rem 0;
  }
}

/* ボタン */
.co-btn-ghost-white {
  @apply text-co-gray-700;
}
</style>
